/**
 * /dashboard/help — centro de ayuda del usuario.
 *
 * Flujo:
 *  1. El usuario escribe su pregunta → la mandamos a /api/support/ask.
 *  2. Mostramos la respuesta IA + un botón "Abrir ticket con esta duda" para
 *     escalar si la respuesta no le sirve.
 *  3. Debajo: acordeón de FAQs y lista de tickets propios.
 *  4. Al pulsar un ticket: vista de conversación con form de respuesta.
 *
 * `?ticket=<id>` en la URL abre directamente la conversación (lo usan los
 * emails de notificación).
 */
import { useState, useMemo, useEffect, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  HelpCircle,
  Sparkles,
  Send,
  Loader2,
  ChevronDown,
  MessageSquare,
  ArrowLeft,
  CheckCircle2,
  Clock,
  XCircle,
} from 'lucide-react';
import { api } from '../lib/api';
import { cn } from '../lib/cn';

type Faq = {
  id: string;
  question: string;
  answer: string;
  category: string;
};

type Ticket = {
  id: string;
  subject: string;
  status: 'open' | 'answered' | 'closed';
  createdAt: string;
  updatedAt: string;
};

type TicketMessage = {
  id: string;
  ticketId: string;
  authorRole: 'user' | 'admin';
  body: string;
  createdAt: string;
};

type TicketDetail = Ticket & {
  messages: TicketMessage[];
  user: { id: string; email: string; name: string | null };
};

export function Help() {
  const [params, setParams] = useSearchParams();
  const openTicketId = params.get('ticket');

  if (openTicketId) {
    return <TicketDetailView ticketId={openTicketId} onBack={() => setParams({})} />;
  }

  // Support pre-filling the ticket form via ?subject=...&body=...
  const preSubject = params.get('subject') ?? '';
  const preBody    = params.get('body')    ?? '';

  return (
    <HelpIndex
      onOpenTicket={(id) => setParams({ ticket: id })}
      preSubject={preSubject}
      preBody={preBody}
    />
  );
}

/* ── Index view ───────────────────────────────────────────── */

function HelpIndex({
  onOpenTicket,
  preSubject = '',
  preBody = '',
}: {
  onOpenTicket: (id: string) => void;
  preSubject?: string;
  preBody?: string;
}) {
  const [question, setQuestion] = useState('');
  const [aiAnswer, setAiAnswer] = useState<{ answer: string; resolved: boolean } | null>(null);
  // Auto-open the ticket form if the caller pre-filled subject/body
  const [showTicketForm, setShowTicketForm] = useState(() => !!(preSubject || preBody));

  const askMut = useMutation({
    mutationFn: (q: string) => api<{ answer: string; resolved: boolean }>('/api/support/ask', {
      method: 'POST',
      body: JSON.stringify({ question: q }),
    }),
    onSuccess: (data) => {
      setAiAnswer(data);
      setShowTicketForm(false);
    },
  });

  const faqsQ = useQuery({
    queryKey: ['support', 'faqs'],
    queryFn: () => api<{ faqs: Faq[] }>('/api/support/faqs'),
    staleTime: 60 * 60_000,
  });

  const ticketsQ = useQuery({
    queryKey: ['support', 'tickets'],
    queryFn: () => api<{ tickets: Ticket[] }>('/api/support/tickets'),
  });

  const faqByCat = useMemo(() => {
    const map = new Map<string, Faq[]>();
    for (const f of faqsQ.data?.faqs ?? []) {
      if (!map.has(f.category)) map.set(f.category, []);
      map.get(f.category)!.push(f);
    }
    return Array.from(map.entries());
  }, [faqsQ.data]);

  function handleAsk(e: FormEvent) {
    e.preventDefault();
    if (!question.trim()) return;
    askMut.mutate(question.trim());
  }

  return (
    <div className="space-y-10">
      <header className="flex items-center gap-3">
        <span className="h-10 w-10 inline-flex items-center justify-center rounded-xl bg-brand-50 text-brand-700">
          <HelpCircle size={20} />
        </span>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Centro de ayuda</h1>
          <p className="text-sm text-slate-500">Pregunta lo que necesites. Si no encontramos respuesta, abrimos un ticket.</p>
        </div>
      </header>

      {/* ── Search / AI ask ─────────────────────────────────── */}
      <section className="rounded-2xl bg-gradient-to-br from-brand-50 via-white to-white border border-brand-100 p-6">
        <form onSubmit={handleAsk} className="flex gap-2">
          <input
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="¿Cómo conecto Instagram? ¿Cómo cambio de plan?"
            className="input flex-1"
            maxLength={500}
          />
          <button
            type="submit"
            disabled={!question.trim() || askMut.isPending}
            className="btn-primary inline-flex items-center gap-1.5 px-5 whitespace-nowrap"
          >
            {askMut.isPending ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
            Preguntar
          </button>
        </form>

        {askMut.isError && (
          <p className="mt-3 text-sm text-red-600">No hemos podido responder ahora. Inténtalo de nuevo o abre un ticket.</p>
        )}

        {aiAnswer && (
          <div className="mt-5 rounded-xl border border-slate-200 bg-white p-5">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={14} className="text-brand-600" />
              <span className="text-xs font-semibold text-brand-700 uppercase tracking-wider">Respuesta IA</span>
              {aiAnswer.resolved ? (
                <span className="ml-auto text-[11px] inline-flex items-center gap-1 text-emerald-600 font-medium">
                  <CheckCircle2 size={12} /> Resuelta
                </span>
              ) : (
                <span className="ml-auto text-[11px] inline-flex items-center gap-1 text-amber-600 font-medium">
                  Sin certeza
                </span>
              )}
            </div>
            <p className="text-slate-700 leading-relaxed whitespace-pre-line">{aiAnswer.answer}</p>

            <div className="mt-4 flex flex-wrap gap-2 items-center">
              <button
                type="button"
                onClick={() => setShowTicketForm((v) => !v)}
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition',
                  aiAnswer.resolved
                    ? 'bg-slate-50 text-slate-600 hover:bg-slate-100 border border-slate-200'
                    : 'bg-brand-600 text-white hover:bg-brand-700',
                )}
              >
                <MessageSquare size={13} />
                {aiAnswer.resolved ? 'Aun así abrir ticket' : 'Abrir ticket con esta duda'}
              </button>
            </div>

            {showTicketForm && (
              <TicketComposer
                initialBody={`Pregunta original:\n${question}\n\nLa IA respondió:\n${aiAnswer.answer}\n\nLo que necesito:\n`}
                initialSubject={question.slice(0, 80)}
                onCreated={(id) => {
                  setShowTicketForm(false);
                  setAiAnswer(null);
                  setQuestion('');
                  onOpenTicket(id);
                }}
              />
            )}
          </div>
        )}

        {!aiAnswer && (
          <div className="mt-4 text-xs text-slate-500">
            ¿Prefieres escribir directamente al equipo?{' '}
            <button
              type="button"
              onClick={() => setShowTicketForm(true)}
              className="text-brand-600 hover:text-brand-700 font-medium"
            >
              Abrir un ticket
            </button>
          </div>
        )}

        {showTicketForm && !aiAnswer && (
          <div className="mt-4">
            <TicketComposer
              initialSubject={preSubject}
              initialBody={preBody}
              onCreated={(id) => { setShowTicketForm(false); onOpenTicket(id); }}
            />
          </div>
        )}
      </section>

      {/* ── My tickets ──────────────────────────────────────── */}
      {(ticketsQ.data?.tickets.length ?? 0) > 0 && (
        <section>
          <h2 className="text-lg font-semibold text-slate-900 mb-3">Mis tickets</h2>
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            {ticketsQ.data!.tickets.map((t, idx) => (
              <button
                key={t.id}
                type="button"
                onClick={() => onOpenTicket(t.id)}
                className={cn(
                  'w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition',
                  idx > 0 && 'border-t border-slate-100',
                )}
              >
                <StatusPill status={t.status} />
                <span className="flex-1 min-w-0">
                  <span className="block font-medium text-slate-900 truncate">{t.subject}</span>
                  <span className="block text-xs text-slate-500">
                    Actualizado {new Date(t.updatedAt).toLocaleDateString('es-ES')}
                  </span>
                </span>
                <ChevronDown size={15} className="text-slate-400 -rotate-90" />
              </button>
            ))}
          </div>
        </section>
      )}

      {/* ── FAQs ────────────────────────────────────────────── */}
      <section>
        <h2 className="text-lg font-semibold text-slate-900 mb-3">Preguntas frecuentes</h2>
        {faqsQ.isLoading ? (
          <div className="rounded-xl border border-slate-200 bg-white p-6 text-slate-400 text-sm flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Cargando…
          </div>
        ) : (
          <div className="space-y-6">
            {faqByCat.map(([cat, faqs]) => (
              <div key={cat}>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 px-1">{cat}</h3>
                <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                  {faqs.map((f, idx) => (
                    <details
                      key={f.id}
                      className={cn('group', idx > 0 && 'border-t border-slate-100')}
                    >
                      <summary className="cursor-pointer list-none flex items-center justify-between gap-3 px-4 py-3 hover:bg-slate-50 transition">
                        <span className="font-medium text-slate-800 text-sm">{f.question}</span>
                        <ChevronDown size={14} className="text-slate-400 transition-transform group-open:rotate-180 shrink-0" />
                      </summary>
                      <div className="px-4 pb-4 text-sm text-slate-600 leading-relaxed">{f.answer}</div>
                    </details>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

/* ── Ticket composer ─────────────────────────────────────── */

function TicketComposer({
  initialSubject = '',
  initialBody = '',
  onCreated,
}: {
  initialSubject?: string;
  initialBody?: string;
  onCreated: (id: string) => void;
}) {
  const [subject, setSubject] = useState(initialSubject);
  const [body, setBody] = useState(initialBody);
  const qc = useQueryClient();

  // Reset when initial values change (cuando llega una nueva respuesta IA)
  useEffect(() => {
    setSubject(initialSubject);
    setBody(initialBody);
  }, [initialSubject, initialBody]);

  const mut = useMutation({
    mutationFn: () =>
      api<{ ticket: Ticket }>('/api/support/tickets', {
        method: 'POST',
        body: JSON.stringify({ subject, body }),
      }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['support', 'tickets'] });
      onCreated(data.ticket.id);
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (subject.trim() && body.trim()) mut.mutate();
      }}
      className="space-y-3 mt-4 pt-4 border-t border-slate-100"
    >
      <div>
        <label className="label">Asunto</label>
        <input
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          maxLength={200}
          required
          placeholder="Resume en una frase"
          className="input"
        />
      </div>
      <div>
        <label className="label">Mensaje</label>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          required
          rows={6}
          maxLength={5000}
          placeholder="Cuéntanos qué te pasa. Cuantos más detalles, mejor te podemos ayudar."
          className="input"
        />
      </div>
      {mut.isError && <p className="text-sm text-red-600">No hemos podido crear el ticket. Inténtalo de nuevo.</p>}
      <button
        type="submit"
        disabled={mut.isPending || !subject.trim() || !body.trim()}
        className="btn-primary inline-flex items-center gap-1.5"
      >
        {mut.isPending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
        Enviar ticket
      </button>
    </form>
  );
}

/* ── Ticket detail view ──────────────────────────────────── */

function TicketDetailView({ ticketId, onBack }: { ticketId: string; onBack: () => void }) {
  const [reply, setReply] = useState('');
  const qc = useQueryClient();

  const ticketQ = useQuery({
    queryKey: ['support', 'ticket', ticketId],
    queryFn: () => api<{ ticket: TicketDetail }>(`/api/support/tickets/${ticketId}`),
  });

  // Cuando el detalle se carga, el server marca `user_last_seen_at = now()`,
  // por lo que el contador de no leídos baja. Invalidamos para refrescar el badge.
  useEffect(() => {
    if (ticketQ.data) {
      qc.invalidateQueries({ queryKey: ['support', 'unread-count'] });
    }
  }, [ticketQ.data, qc]);

  const replyMut = useMutation({
    mutationFn: () =>
      api<{ message: TicketMessage }>(`/api/support/tickets/${ticketId}/messages`, {
        method: 'POST',
        body: JSON.stringify({ body: reply }),
      }),
    onSuccess: () => {
      setReply('');
      qc.invalidateQueries({ queryKey: ['support', 'ticket', ticketId] });
      qc.invalidateQueries({ queryKey: ['support', 'tickets'] });
      qc.invalidateQueries({ queryKey: ['support', 'unread-count'] });
    },
  });

  const closeMut = useMutation({
    mutationFn: () =>
      api(`/api/support/tickets/${ticketId}/close`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['support', 'ticket', ticketId] });
      qc.invalidateQueries({ queryKey: ['support', 'tickets'] });
    },
  });

  if (ticketQ.isLoading) {
    return <div className="text-slate-400 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Cargando…</div>;
  }
  if (!ticketQ.data) {
    return (
      <div>
        <button onClick={onBack} className="text-sm text-brand-600 inline-flex items-center gap-1">
          <ArrowLeft size={14} /> Volver
        </button>
        <p className="mt-4 text-slate-500">No se encontró el ticket.</p>
      </div>
    );
  }

  const ticket = ticketQ.data.ticket;
  const closed = ticket.status === 'closed';

  return (
    <div className="space-y-6">
      <button onClick={onBack} className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
        <ArrowLeft size={14} /> Volver a ayuda
      </button>

      <header className="flex items-start gap-3">
        <StatusPill status={ticket.status} />
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold text-slate-900">{ticket.subject}</h1>
          <p className="text-xs text-slate-500 mt-1">
            Abierto el {new Date(ticket.createdAt).toLocaleString('es-ES', { dateStyle: 'medium', timeStyle: 'short' })}
          </p>
        </div>
        {!closed && (
          <button
            type="button"
            onClick={() => closeMut.mutate()}
            disabled={closeMut.isPending}
            className="btn-secondary text-xs"
          >
            Cerrar ticket
          </button>
        )}
      </header>

      {/* Mensajes */}
      <div className="space-y-3">
        {ticket.messages.map((m) => (
          <MessageBubble key={m.id} message={m} />
        ))}
      </div>

      {/* Reply */}
      {!closed ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (reply.trim()) replyMut.mutate();
          }}
          className="rounded-xl border border-slate-200 bg-white p-4"
        >
          <textarea
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            rows={4}
            maxLength={5000}
            placeholder="Escribe tu respuesta…"
            className="input"
          />
          {replyMut.isError && <p className="mt-2 text-sm text-red-600">No hemos podido enviar tu mensaje.</p>}
          <div className="mt-3 flex justify-end">
            <button
              type="submit"
              disabled={!reply.trim() || replyMut.isPending}
              className="btn-primary inline-flex items-center gap-1.5"
            >
              {replyMut.isPending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
              Enviar respuesta
            </button>
          </div>
        </form>
      ) : (
        <p className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600 text-center">
          Este ticket está cerrado. Si necesitas algo más, abre uno nuevo desde el centro de ayuda.
        </p>
      )}
    </div>
  );
}

/* ── Components ──────────────────────────────────────────── */

function MessageBubble({ message }: { message: TicketMessage }) {
  const isUser = message.authorRole === 'user';
  return (
    <div className={cn('flex', isUser ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[80%] rounded-2xl px-4 py-3',
          isUser ? 'bg-brand-600 text-white' : 'bg-white border border-slate-200 text-slate-800',
        )}
      >
        <p className="whitespace-pre-line text-sm leading-relaxed">{message.body}</p>
        <p className={cn('text-[10px] mt-1.5', isUser ? 'text-brand-100' : 'text-slate-400')}>
          {isUser ? 'Tú' : 'Zettapost'} ·{' '}
          {new Date(message.createdAt).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' })}
        </p>
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: 'open' | 'answered' | 'closed' }) {
  if (status === 'open') {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
        <Clock size={11} /> Abierto
      </span>
    );
  }
  if (status === 'answered') {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-brand-700 bg-brand-50 border border-brand-200 px-2 py-0.5 rounded-full">
        <CheckCircle2 size={11} /> Respondido
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-full">
      <XCircle size={11} /> Cerrado
    </span>
  );
}
